import { Component, ChangeDetectionStrategy } from '@angular/core';
import { Location } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { PanelModule } from 'primeng/panel';
import { ButtonModule } from 'primeng/button';
import { Textarea } from 'primeng/textarea';
import { MessageModule } from 'primeng/message';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { LinkError } from '@apollo/client';
import { firstValueFrom } from 'rxjs';
import { RoutingService } from 'src/app/services/routing.service';
import { SubtitleService } from 'src/app/services/subtitle.service';
import { UserService } from 'src/app/services/user.service';
import { RegisterUserAmazonTrackingsGQL } from 'src/gql/generated';
import { ParseError, ParsedTrackingLine, ParseResult, parseTsvCsv } from './register.parser';
import { gqlErrors } from 'src/app/utils/gql-errors';

interface FlashMessage {
  severity: 'success' | 'info' | 'warn' | 'error';
  summary: string;
  detail: string;
}

@Component({
  selector: 'app-amazon-tracking-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.sass'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FormsModule, RouterModule, PanelModule, ButtonModule, Textarea, MessageModule, TableModule, TagModule],
})
export class AmazonTrackingRegisterComponent {
  inputText = '';
  parseResult: ParseResult | null = null;
  isLoading = false;
  messages: FlashMessage[] = [];

  constructor(
    private location: Location,
    private routingService: RoutingService,
    private subtitleService: SubtitleService,
    private userService: UserService,
    private registerGQL: RegisterUserAmazonTrackingsGQL,
  ) {
    this.routingService.loginRequired();
    this.userService.amazonPriceTrackingRequired();
    this.subtitleService.setSubTitle('Amazon追跡 - 一括登録');
  }

  onPreview(): void {
    this.messages = [];
    if (!this.inputText.trim()) {
      this.parseResult = null;
      return;
    }
    this.parseResult = parseTsvCsv(this.inputText);
  }

  onClear(): void {
    this.inputText = '';
    this.parseResult = null;
    this.messages = [];
  }

  goBack(): void {
    this.location.back();
  }

  get validCount(): number {
    return this.parseResult?.valid.length ?? 0;
  }

  get errorCount(): number {
    return this.parseResult?.errors.length ?? 0;
  }

  get canSubmit(): boolean {
    return !this.isLoading && this.validCount > 0;
  }

  async onSubmit(): Promise<void> {
    this.messages = [];

    // 念のため最新状態で再パース
    this.parseResult = parseTsvCsv(this.inputText);

    if (this.parseResult.valid.length === 0) {
      this.messages = [
        {
          severity: 'error',
          summary: 'エラー',
          detail: '登録可能な行がありません。入力内容を確認してください。',
        },
      ];
      return;
    }

    const inputs = this.parseResult.valid.map((v) => v.input);
    this.isLoading = true;

    try {
      const result = await firstValueFrom(this.registerGQL.mutate({ variables: { inputs } }));

      if (gqlErrors(result)?.length > 0) {
        this.messages = [this.buildErrorMessage(gqlErrors(result)[0])];
        return;
      }

      const data = result.data?.registerUserAmazonTrackings;
      if (!data) {
        this.messages = [
          {
            severity: 'error',
            summary: 'エラー',
            detail: '登録処理でエラーが発生しました。再度お試しください。',
          },
        ];
        return;
      }

      const flash: FlashMessage[] = [];
      if (data.registeredCount > 0) {
        flash.push({
          severity: 'success',
          summary: '成功',
          detail: `${data.registeredCount}件を登録しました`,
        });
      }
      if (data.skippedAsins.length > 0) {
        flash.push({
          severity: 'warn',
          summary: 'スキップ',
          detail: this.buildSkippedDetail(data.skippedAsins),
        });
      }
      if (flash.length === 0) {
        flash.push({
          severity: 'info',
          summary: '結果',
          detail: '登録対象の変更はありませんでした。',
        });
      }
      this.messages = flash;

      if (data.registeredCount > 0) {
        this.inputText = '';
        this.parseResult = null;
      }
    } catch (error) {
      this.messages = [this.buildExceptionMessage(error)];
    } finally {
      this.isLoading = false;
    }
  }

  getSpapiReconnectLink(): string {
    return 'https://sellercentral.amazon.co.jp/apps/authorize/consent?application_id=amzn1.sellerapps.app.2061b3cc-fc3b-48cc-8b52-fd333bc47f68&state=link';
  }

  trackByLine(_index: number, item: ParsedTrackingLine | ParseError): number {
    return item.lineNo;
  }

  private buildSkippedDetail(skipped: string[]): string {
    const head = skipped.slice(0, 5).join(', ');
    if (skipped.length <= 5) {
      return `既に登録済みのためスキップしました: ${head}`;
    }
    return `既に登録済みのためスキップしました: ${head} ほか${skipped.length - 5}件`;
  }

  private buildErrorMessage(error: { message?: string; extensions?: { code?: string } }): FlashMessage {
    const msg = error.message ?? '';
    if (msg.includes('SP-API token is invalid')) {
      return {
        severity: 'error',
        summary: 'SP-APIトークンが無効です',
        detail: 'Amazon連携の再認証が必要です。再認証リンクから Amazon 連携を行ってください。',
      };
    }
    if (msg.includes('duplicate ASIN in input')) {
      return {
        severity: 'error',
        summary: 'エラー',
        detail: '同じ ASIN が複数回含まれています。プレビューを確認してください。',
      };
    }
    if (error.extensions?.code === 'E0001') {
      return {
        severity: 'error',
        summary: 'エラー',
        detail: 'サーバー内部エラーが発生しました。管理者にお問い合わせください。',
      };
    }
    return {
      severity: 'error',
      summary: 'エラー',
      detail: msg || '登録に失敗しました。',
    };
  }

  // GraphQL エラーは errorPolicy 'all' で結果の error に入って返るので、ここに来るのは通信エラーとそれ以外の例外だけ
  private buildExceptionMessage(error: unknown): FlashMessage {
    if (LinkError.is(error)) {
      return {
        severity: 'error',
        summary: 'ネットワークエラー',
        detail: 'インターネット接続を確認してください。',
      };
    }
    if (error instanceof Error && error.message) {
      return { severity: 'error', summary: 'エラー', detail: error.message };
    }
    return {
      severity: 'error',
      summary: 'エラー',
      detail: '登録に失敗しました。しばらく時間をおいて再度お試しください。',
    };
  }
}
